import { prefersReducedMotion, seededRandom } from '../../site/utils/random';

/*
 * Hero "hologram": three wireframe buildings that assemble from ruins, light up and
 * slide away, rotating through 360°. Plain 2D canvas with a hand-rolled projection.
 * Ported from the redesign mockup — startHoloScene() returns a cleanup function.
 */

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const lerp = (a, b, t) => a + (b - a) * t;
const easeOut = (t) => 1 - Math.pow(1 - t, 3);
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

// 3x3 matrix helpers (row-major)
const mul = (a, b) => [
  a[0] * b[0] + a[1] * b[3] + a[2] * b[6], a[0] * b[1] + a[1] * b[4] + a[2] * b[7], a[0] * b[2] + a[1] * b[5] + a[2] * b[8],
  a[3] * b[0] + a[4] * b[3] + a[5] * b[6], a[3] * b[1] + a[4] * b[4] + a[5] * b[7], a[3] * b[2] + a[4] * b[5] + a[5] * b[8],
  a[6] * b[0] + a[7] * b[3] + a[8] * b[6], a[6] * b[1] + a[7] * b[4] + a[8] * b[7], a[6] * b[2] + a[7] * b[5] + a[8] * b[8],
];
const rotY = (a) => { const c = Math.cos(a), s = Math.sin(a); return [c, 0, s, 0, 1, 0, -s, 0, c]; };
const rotX = (a) => { const c = Math.cos(a), s = Math.sin(a); return [1, 0, 0, 0, c, -s, 0, s, c]; };
const rotZ = (a) => { const c = Math.cos(a), s = Math.sin(a); return [c, -s, 0, s, c, 0, 0, 0, 1]; };
const mix = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const rgb = (c) => `rgb(${c[0] | 0},${c[1] | 0},${c[2] | 0})`;

const RUBBLE = [205, 140, 92];
const MINT = [143, 224, 196];
const WHITE = [238, 255, 250];
const GOLD = [240, 198, 107];
const TEAL = [40, 120, 98];

// Timeline (seconds inside one building's cycle)
const BUILD = 8.5;
const PERIOD = 13.0;
const SLIDE = 0.9;
const T_RUIN = 0.9;
const T_END_BUILD = T_RUIN + BUILD;
const REDUCED_MOTION_TAU = T_RUIN + BUILD + 1.0;

// Camera
const PITCH = 0.31;
const DISTANCE = 720;
const FOCAL = 640;

// Part kinds: f=floor, a=balcony, m=mechanical, b=beacon, t=tree, s=ruin stub, p=platform
const STYLE = {
  f: { fa: 0.16, ta: 0.26, ea: 0.8, col: TEAL },
  a: { fa: 0.24, ta: 0.3, ea: 0.7, col: [60, 150, 120] },
  m: { fa: 0.28, ta: 0.34, ea: 0.75, col: [70, 140, 150] },
  b: { fa: 0.55, ta: 0.7, ea: 1, col: GOLD },
  t: { fa: 0.3, ta: 0.4, ea: 0.8, col: [60, 190, 110] },
  s: { fa: 0.12, ta: 0.16, ea: 0.7, col: RUBBLE },
  p: { fa: 0.3, ta: 0.4, ea: 0.85, col: [30, 90, 80] },
};

// Box faces: corner indices, axis, sign, and (for walls) window grid origin/u/v corners
const FACES = [
  { r: [1, 3, 7, 5], ax: 0, s: 1, o: 1, u: 5, v: 3 },
  { r: [0, 4, 6, 2], ax: 0, s: -1, o: 0, u: 4, v: 2 },
  { r: [2, 3, 7, 6], ax: 1, s: 1 },
  { r: [0, 1, 5, 4], ax: 1, s: -1 },
  { r: [4, 5, 7, 6], ax: 2, s: 1, o: 4, u: 5, v: 6 },
  { r: [0, 1, 3, 2], ax: 2, s: -1, o: 0, u: 1, v: 2 },
];

/* ---------- buildings ---------- */

// Adds the scatter/assembly parameters, ruin stubs and "construction rain" to a building.
function finish(building, seed) {
  const R = seededRandom(seed);
  building.parts.forEach((p) => {
    p.ts = clamp((p.y - p.hh) / building.H, 0, 1) * 0.72 + R() * 0.02;
    p.dur = 0.2 + R() * 0.04;
    const a = R() * 6.2832;
    const r = building.dr * (0.3 + R() * 0.7);
    p.dx = Math.cos(a) * r;
    p.dz = Math.sin(a) * r;
    p.dy = 2 + R() * 14;
    p.rx = (R() - 0.5) * 3;
    p.ry = R() * 6.28;
    p.rz = (R() - 0.5) * 3;
    p.sp = (R() - 0.5) * 0.9;
    p.ds = 0.2 + R() * 0.16;
    p.ph = R() * 6.28;
    p.nwx = Math.max(3, Math.round((p.hw * 2) / 11));
    p.nwz = Math.max(3, Math.round((p.hd * 2) / 11));
  });

  building.stubs = [];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * 6.2832 + R() * 0.5;
    const r = building.fp * (0.55 + R() * 0.55);
    const h = 3 + R() * 14;
    building.stubs.push({ k: 's', x: Math.cos(a) * r, y: h, z: Math.sin(a) * r, hw: 2.5 + R() * 4, hh: h, hd: 2.5 + R() * 4, yaw: R() * 3 });
  }

  building.rain = [];
  for (let j = 0; j < 60; j++) {
    const a = R() * 6.2832;
    const r = building.fp * (0.5 + R() * 0.9);
    building.rain.push({ x: Math.cos(a) * r, z: Math.sin(a) * r, spd: 50 + R() * 90, ph: R() * 400, len: 14 + R() * 30 });
  }
  return building;
}

function steppedTower() {
  const P = [];
  let y = 0;
  const addFloors = (hw, n) => {
    for (let i = 0; i < n; i++) P.push({ k: 'f', x: 0, y: y + 8.5 * i + 3.6, z: 0, hw, hh: 3.6, hd: hw, yaw: 0, win: 1 });
    y += 8.5 * n;
  };
  P.push({ k: 'f', x: 0, y: 7, z: 0, hw: 72, hh: 7, hd: 72, yaw: 0, win: 1 });
  y = 14.5;
  addFloors(58, 12);
  addFloors(45, 12);
  addFloors(31, 10);
  P.push({ k: 'm', x: 0, y: y + 3, z: 0, hw: 22, hh: 3, hd: 22, yaw: 0 });
  y += 6;
  P.push({ k: 'm', x: 0, y: y + 20, z: 0, hw: 2.2, hh: 20, hd: 2.2, yaw: 0 });
  y += 40;
  P.push({ k: 'b', x: 0, y: y + 2, z: 0, hw: 3.4, hh: 2, hd: 3.4, yaw: 0 });
  return finish({ name: 'Stepped tower', parts: P, H: y + 4, pivot: (y + 4) * 0.48, zoom: 1, fp: 84, dr: 150, ring: 84 }, 11);
}

function courtyard() {
  const P = [];
  const S = 1.7;
  const n = 12;
  const pitch = 8.4;
  const base = 3;
  const wings = [[-50, 0, 16, 56], [50, 0, 16, 56], [0, -41, 36, 15]];
  for (let i = 0; i < n; i++) {
    wings.forEach((w) => P.push({ k: 'f', x: w[0] * S, y: (base + pitch * i + 3.5) * S, z: w[1] * S, hw: w[2] * S, hh: 3.5 * S, hd: w[3] * S, yaw: 0, win: 1 }));
  }
  for (let i = 0; i < n; i++) {
    [-1, 1].forEach((side) => [-24, 24].forEach((zz) => P.push({ k: 'a', x: side * 71 * S, y: (base + pitch * i + 1) * S, z: zz * S, hw: 5 * S, hh: 0.9 * S, hd: 9 * S, yaw: 0 })));
  }
  [[-18, 12], [0, 22], [18, 10], [-8, 36], [10, -6]].forEach((t) => P.push({ k: 't', x: t[0] * S, y: 5 * S, z: t[1] * S, hw: 3.4 * S, hh: 5 * S, hd: 3.4 * S, yaw: 0 }));
  const top = (base + pitch * n) * S;
  P.push({ k: 'm', x: -50 * S, y: top + 3 * S, z: -18 * S, hw: 8 * S, hh: 3 * S, hd: 8 * S, yaw: 0 });
  P.push({ k: 'm', x: 50 * S, y: top + 3 * S, z: 18 * S, hw: 8 * S, hh: 3 * S, hd: 8 * S, yaw: 0 });
  P.push({ k: 'b', x: 0, y: top + 4 * S, z: -41 * S, hw: 3 * S, hh: 2.4 * S, hd: 3 * S, yaw: 0 });
  return finish({ name: 'Courtyard residences', parts: P, H: top + 9 * S, pivot: (top + 9 * S) * 0.5, zoom: 1.2, fp: 125, dr: 190, ring: 128 }, 23);
}

function twistTower() {
  const P = [];
  const n = 36;
  const pitch = 8.1;
  P.push({ k: 'f', x: 0, y: 5, z: 0, hw: 50, hh: 5, hd: 50, yaw: 0, win: 1 });
  let y = 11;
  for (let i = 0; i < n; i++) {
    const w = 36 * (1 - (0.4 * i) / (n - 1));
    P.push({ k: 'f', x: 0, y: y + pitch * i + 3.3, z: 0, hw: w, hh: 3.3, hd: w, yaw: i * 0.078, win: 1 });
  }
  y += pitch * n;
  P.push({ k: 'm', x: 0, y: y + 2.5, z: 0, hw: 14, hh: 2.5, hd: 14, yaw: n * 0.078 });
  y += 5;
  P.push({ k: 'm', x: 0, y: y + 22, z: 0, hw: 1.8, hh: 22, hd: 1.8, yaw: 0 });
  y += 44;
  P.push({ k: 'b', x: 0, y: y + 2, z: 0, hw: 3, hh: 2, hd: 3, yaw: 0 });
  return finish({ name: 'Twist tower', parts: P, H: y + 4, pivot: (y + 4) * 0.48, zoom: 1.02, fp: 62, dr: 130, ring: 60 }, 37);
}

let buildingsCache = null;
const getBuildings = () => {
  if (!buildingsCache) buildingsCache = [steppedTower(), courtyard(), twistTower()];
  return buildingsCache;
};

/* ---------- scene ---------- */

export function startHoloScene(canvas, hud = {}) {
  const ctx = canvas?.getContext?.('2d');
  if (!ctx) return () => {};

  const reduce = prefersReducedMotion();
  const BUILDINGS = getBuildings();

  let W = 520;
  let H = 440;
  let DPR = 1;

  // per-frame projection state
  let viewScale = 1;
  let cx = 0;
  let cy = 0;
  let off = 0;
  let F = FOCAL;
  let Rcam = rotY(0);
  let pivotY = 0;
  let ga = 1;
  const PX = new Array(8);
  const PY = new Array(8);

  const fit = () => {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width) return false;
    DPR = Math.min(window.devicePixelRatio || 1, 2);
    W = rect.width;
    H = rect.height;
    canvas.width = Math.round(W * DPR);
    canvas.height = Math.round(H * DPR);
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    return true;
  };

  const cam = (x, y, z) => {
    y -= pivotY;
    const R = Rcam;
    return [R[0] * x + R[1] * y + R[2] * z, R[3] * x + R[4] * y + R[5] * z, R[6] * x + R[7] * y + R[8] * z + DISTANCE];
  };
  const screen = (c) => {
    const k = F / c[2];
    return [cx + off + c[0] * k, cy - c[1] * k];
  };
  const tracePath = (ix) => {
    ctx.beginPath();
    ctx.moveTo(PX[ix[0]], PY[ix[0]]);
    ctx.lineTo(PX[ix[1]], PY[ix[1]]);
    ctx.lineTo(PX[ix[2]], PY[ix[2]]);
    ctx.lineTo(PX[ix[3]], PY[ix[3]]);
    ctx.closePath();
  };

  function drawItem(it) {
    const pt = it.pt;
    const st = STYLE[pt.k];
    const M = it.M;
    const sc = it.sc;
    const C = it.C;
    const ha = [M[0] * pt.hw * sc, M[3] * pt.hw * sc, M[6] * pt.hw * sc];
    const hb = [M[1] * pt.hh * sc, M[4] * pt.hh * sc, M[7] * pt.hh * sc];
    const hc = [M[2] * pt.hd * sc, M[5] * pt.hd * sc, M[8] * pt.hd * sc];

    for (let i = 0; i < 8; i++) {
      const sx = i & 1 ? 1 : -1;
      const sy = i & 2 ? 1 : -1;
      const sz = i & 4 ? 1 : -1;
      const x = C[0] + sx * ha[0] + sy * hb[0] + sz * hc[0];
      const y = C[1] + sx * ha[1] + sy * hb[1] + sz * hc[1];
      const z = C[2] + sx * ha[2] + sy * hb[2] + sz * hc[2];
      const k = F / z;
      PX[i] = cx + off + x * k;
      PY[i] = cy - y * k;
    }

    const halves = [ha, hb, hc];
    const fillColor = rgb(it.fc);
    const edgeColor = rgb(it.ec);
    const A = it.a;

    for (let f = 0; f < 6; f++) {
      const face = FACES[f];
      const h = halves[face.ax];
      const s = face.s;
      // back-face cull: skip faces whose outward normal points away from the camera
      if (s * h[0] * (C[0] + s * h[0]) + s * h[1] * (C[1] + s * h[1]) + s * h[2] * (C[2] + s * h[2]) >= 0) continue;

      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = ga * A * (face.ax === 1 ? (s > 0 ? st.ta : st.fa * 0.5) : st.fa);
      ctx.fillStyle = fillColor;
      tracePath(face.r);
      ctx.fill();

      if (pt.win && face.ax !== 1 && it.lit > 0.01) {
        const { o, u, v } = face;
        const Ux = PX[u] - PX[o];
        const Uy = PY[u] - PY[o];
        const Vx = PX[v] - PX[o];
        const Vy = PY[v] - PY[o];
        const count = face.ax === 0 ? pt.nwz : pt.nwx;
        const flicker = 0.62 + 0.38 * Math.sin(it.t * 1.6 + pt.ph * 3 + f);
        ctx.globalAlpha = ga * A * it.lit * flicker * 0.9;
        ctx.fillStyle = '#f0c66b';
        ctx.beginPath();
        for (let w = 0; w < count; w++) {
          const u0 = (w + 0.2) / count;
          const u1 = (w + 0.8) / count;
          const v0 = 0.27;
          const v1 = 0.73;
          ctx.moveTo(PX[o] + u0 * Ux + v0 * Vx, PY[o] + u0 * Uy + v0 * Vy);
          ctx.lineTo(PX[o] + u1 * Ux + v0 * Vx, PY[o] + u1 * Uy + v0 * Vy);
          ctx.lineTo(PX[o] + u1 * Ux + v1 * Vx, PY[o] + u1 * Uy + v1 * Vy);
          ctx.lineTo(PX[o] + u0 * Ux + v1 * Vx, PY[o] + u0 * Uy + v1 * Vy);
          ctx.closePath();
        }
        ctx.fill();
      }

      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = ga * A * Math.min(1, st.ea + it.heat * 0.4);
      ctx.strokeStyle = edgeColor;
      ctx.lineWidth = it.heat > 0.2 ? 1.6 : 1.05;
      tracePath(face.r);
      ctx.stroke();
      if (it.heat > 0.25) {
        ctx.globalAlpha = ga * A * it.heat * 0.35;
        ctx.lineWidth = 4;
        tracePath(face.r);
        ctx.stroke();
      }
    }
    ctx.globalCompositeOperation = 'source-over';
  }

  // Positions one part along its fly-in from the scattered "ruin" pose to its final slot.
  function itemFor(building, pt, p, t, tc) {
    const e = clamp((p - pt.ts) / pt.dur, 0, 1);
    const ee = easeOut(e);
    const hover = Math.sin(t * 1.3 + pt.ph) * 2;
    const x = lerp(pt.dx, pt.x, ee);
    const z = lerp(pt.dz, pt.z, ee);
    const y = lerp(pt.dy + hover, pt.y, ee) + Math.sin(Math.PI * ee) * 38 * (1 - ee * 0.2);
    const sc = lerp(pt.ds, 1, ee);
    const yaw = lerp(pt.ry + t * pt.sp, pt.yaw, ee);
    const rx = (1 - ee) * pt.rx;
    const rz = (1 - ee) * pt.rz;
    const since = p > pt.ts + pt.dur ? (p - pt.ts - pt.dur) * BUILD + tc : 0;
    const heat = since > 0 ? Math.exp(-since * 2.3) : 0;

    let ec = e < 1 ? mix(RUBBLE, MINT, ee) : mix(MINT, WHITE, heat);
    if (e > 0 && e < 1) ec = mix(ec, WHITE, Math.sin(Math.PI * e) * 0.45);
    const style = STYLE[pt.k];
    const fcol = e < 1 ? mix(RUBBLE, style.col, ee) : style.col;
    if (pt.k === 'b' && e >= 1) ec = mix(GOLD, WHITE, 0.4 + 0.4 * Math.sin(t * 3));

    const M = mul(Rcam, mul(rotY(yaw), mul(rotX(rx), rotZ(rz))));
    const C = cam(x, y, z);
    const a = lerp(0.5, 1, ee);
    if (tc > 0) {
      // highlight band sweeping up the finished building
      const band = ((t * 0.38) % 1.25) * building.H;
      const d = (y - band) / (building.H * 0.1);
      ec = mix(ec, WHITE, Math.exp(-d * d) * 0.55);
    }
    return { pt, M, C, sc, fc: fcol, ec, a, heat, lit: since > 0 ? clamp((since - 0.15) / 1.1, 0, 1) : 0, t };
  }

  function drawGround(building, fade, t) {
    const R = building.fp * 2.3;
    const N = 6;
    const step = R / N;
    const SEG = 10;
    const bins = [[], [], [], [], []];
    const line = (fx, fz, tx, tz) => {
      for (let s = 0; s < SEG; s++) {
        const x0 = fx + ((tx - fx) * s) / SEG;
        const z0 = fz + ((tz - fz) * s) / SEG;
        const x1 = fx + ((tx - fx) * (s + 1)) / SEG;
        const z1 = fz + ((tz - fz) * (s + 1)) / SEG;
        const r = Math.hypot((x0 + x1) / 2, (z0 + z1) / 2) / R;
        if (r >= 1) continue;
        const bin = Math.min(4, Math.floor((1 - r) * 5));
        const a = screen(cam(x0, 0, z0));
        const c = screen(cam(x1, 0, z1));
        bins[bin].push(a[0], a[1], c[0], c[1]);
      }
    };
    for (let i = -N; i <= N; i++) {
      line(i * step, -R, i * step, R);
      line(-R, i * step, R, i * step);
    }

    ctx.globalCompositeOperation = 'lighter';
    ctx.lineWidth = 1;
    ctx.strokeStyle = '#8fe0c4';
    for (let i = 0; i < 5; i++) {
      ctx.globalAlpha = ga * fade * (0.05 + i * 0.055);
      ctx.beginPath();
      const q = bins[i];
      for (let j = 0; j < q.length; j += 4) {
        ctx.moveTo(q[j], q[j + 1]);
        ctx.lineTo(q[j + 2], q[j + 3]);
      }
      ctx.stroke();
    }

    const r2 = building.ring * 1.12;
    const corners = [[-r2, -r2], [r2, -r2], [r2, r2], [-r2, r2]].map((c) => screen(cam(c[0], 0, c[1])));
    ctx.globalAlpha = ga * fade * (0.45 + 0.15 * Math.sin(t * 2));
    ctx.strokeStyle = '#d4af37';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(corners[0][0], corners[0][1]);
    for (let i = 1; i < 4; i++) ctx.lineTo(corners[i][0], corners[i][1]);
    ctx.closePath();
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }

  function drawRain(building, t, amount) {
    if (amount < 0.02) return;
    const span = building.H + 70;
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineWidth = 1.2;
    building.rain.forEach((drop) => {
      const y = span - ((t * drop.spd + drop.ph) % span);
      const a = screen(cam(drop.x, y, drop.z));
      const c = screen(cam(drop.x, y + drop.len, drop.z));
      const fade = clamp(y / 40, 0, 1);
      ctx.globalAlpha = ga * amount * 0.5 * fade;
      ctx.strokeStyle = '#46d6a0';
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.lineTo(c[0], c[1]);
      ctx.stroke();
      ctx.globalAlpha = ga * amount * 0.95 * fade;
      ctx.fillStyle = '#e8fff4';
      ctx.fillRect(a[0] - 1, a[1] - 1, 2.4, 2.4);
    });
    ctx.globalCompositeOperation = 'source-over';
  }

  // Gold scan plane that rises with construction progress.
  function drawScan(building, p) {
    if (p <= 0 || p >= 1) return;
    const y = (Math.max(0, p - 0.07) / 0.72) * building.H;
    if (y >= building.H) return;
    const r = building.ring * 0.95;
    const c = [[-r, -r], [r, -r], [r, r], [-r, r]].map((q) => screen(cam(q[0], y, q[1])));
    ctx.globalCompositeOperation = 'lighter';
    ctx.beginPath();
    ctx.moveTo(c[0][0], c[0][1]);
    for (let i = 1; i < 4; i++) ctx.lineTo(c[i][0], c[i][1]);
    ctx.closePath();
    ctx.globalAlpha = ga * 0.1;
    ctx.fillStyle = '#f0c66b';
    ctx.fill();
    ctx.globalAlpha = ga * 0.95;
    ctx.strokeStyle = '#f0c66b';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = ga * 0.25;
    ctx.lineWidth = 7;
    ctx.stroke();
    ctx.globalCompositeOperation = 'source-over';
  }

  function drawBuilding(building, p, tc, t, theta, offset, alpha, phase) {
    off = offset;
    ga = alpha;
    F = FOCAL * building.zoom * viewScale;
    pivotY = building.pivot;
    Rcam = mul(rotX(-PITCH), rotY(theta));

    const ruinAlpha = clamp(1 - p * 5, 0, 1);
    drawGround(building, 1, t);

    const items = [];
    const platform = { k: 'p', x: 0, y: -2.5, z: 0, hw: building.fp * 1.05, hh: 2.5, hd: building.fp * 1.05, yaw: 0 };
    items.push({ pt: platform, M: mul(Rcam, rotY(0)), C: cam(0, -2.5, 0), sc: 1, fc: STYLE.p.col, ec: MINT, a: 1, heat: 0, lit: 0, t, z: 1e9 });
    if (ruinAlpha > 0) {
      building.stubs.forEach((s) => {
        items.push({ pt: s, M: mul(Rcam, rotY(s.yaw)), C: cam(s.x, s.y, s.z), sc: 1, fc: RUBBLE, ec: RUBBLE, a: ruinAlpha * 0.9, heat: 0, lit: 0, t });
      });
    }
    building.parts.forEach((pt) => items.push(itemFor(building, pt, p, t, tc)));
    // painter's algorithm: far to near (platform always first)
    items.sort((a, c) => (c.z || c.C[2]) - (a.z || a.C[2]));

    drawRain(building, t, phase === 'build' ? 1 : phase === 'ruin' ? 0.55 : 0.28);
    items.forEach(drawItem);
    drawScan(building, p);
  }

  let hudKey = '';
  const updateHud = (name, state, pct) => {
    const key = name + state + pct;
    if (key === hudKey) return;
    hudKey = key;
    if (hud.state) hud.state.textContent = state;
    if (hud.name) hud.name.textContent = name;
    if (hud.bar) hud.bar.style.width = `${pct}%`;
  };

  function render(tau) {
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.clearRect(0, 0, W, H);

    cx = W / 2;
    cy = H / 2 + 8;
    viewScale = Math.min(1, W / 540, H / 450);
    const slideWidth = W * 0.85;
    const theta = reduce ? 0.75 : tau * 0.27;
    const t = reduce ? 0 : tau;

    const glow = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(W, H) * 0.52);
    glow.addColorStop(0, 'rgba(6,20,19,.62)');
    glow.addColorStop(0.62, 'rgba(6,20,19,.34)');
    glow.addColorStop(1, 'rgba(6,20,19,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    // At most two buildings are on screen: the current one and the one sliding in/out.
    const k0 = Math.floor((tau - T_RUIN) / PERIOD);
    let best = null;
    for (let k = Math.max(0, k0); k <= k0 + 1; k++) {
      const s = tau - T_RUIN - k * PERIOD;
      if (s < -SLIDE || s > PERIOD) continue;
      const building = BUILDINGS[k % 3];
      const enterSide = k === 0 || (k - 1) % 2 === 0 ? 1 : -1;
      const exitDir = k % 2 === 0 ? -1 : 1;

      let p = 0;
      let tc = 0;
      let offset = 0;
      let alpha = 1;
      let phase = 'ruin';
      let state = 'Ruins detected';

      if (s < 0) {
        const u = (s + SLIDE) / SLIDE;
        offset = enterSide * slideWidth * (1 - easeInOut(u));
        alpha = clamp(u * 1.4, 0, 1);
      } else if (s < T_RUIN) {
        p = 0;
      } else if (s < T_END_BUILD) {
        p = (s - T_RUIN) / BUILD;
        phase = 'build';
        state = 'Rebuilding';
      } else {
        p = 1;
        tc = s - T_END_BUILD;
        phase = 'done';
        state = 'Complete';
        if (s > PERIOD - SLIDE) {
          const u = (s - (PERIOD - SLIDE)) / SLIDE;
          offset = exitDir * slideWidth * easeInOut(u);
          alpha = 1 - clamp(u * 1.25, 0, 1);
        }
      }
      if (reduce) {
        p = 1;
        tc = 3;
        phase = 'done';
        state = 'Complete';
        offset = 0;
        alpha = 1;
      }

      drawBuilding(building, p, tc, t, theta, offset, alpha, phase);
      if (!best || alpha >= best.alpha) best = { alpha, name: building.name, state, pct: Math.round(p * 100) };
    }
    if (best) updateHud(best.name, best.state, best.pct);
  }

  /* ---------- lifecycle ---------- */
  let raf = 0;
  let tau = 0;
  let last = 0;
  let visible = true;

  fit();

  const onResize = () => {
    if (fit() && reduce) render(REDUCED_MOTION_TAU);
  };
  let resizeObserver = null;
  if ('ResizeObserver' in window) {
    resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(canvas);
  } else {
    window.addEventListener('resize', onResize);
  }

  let visibilityObserver = null;
  if ('IntersectionObserver' in window) {
    visibilityObserver = new IntersectionObserver((entries) => {
      visible = entries[0].isIntersecting;
    });
    visibilityObserver.observe(canvas);
  }

  if (reduce) {
    render(REDUCED_MOTION_TAU);
  } else {
    const loop = (ts) => {
      raf = requestAnimationFrame(loop);
      // pause while scrolled away or the tab is hidden
      if (!visible || document.hidden) {
        last = 0;
        return;
      }
      const now = ts / 1000;
      const dt = last ? Math.min(now - last, 0.05) : 0;
      last = now;
      tau += dt;
      render(tau);
    };
    raf = requestAnimationFrame(loop);
  }

  return () => {
    cancelAnimationFrame(raf);
    resizeObserver?.disconnect();
    visibilityObserver?.disconnect();
    window.removeEventListener('resize', onResize);
  };
}
